/**
 * The header's search box (review #403): type a name, a Paylocity ID, a facility, a
 * payer type, a department, a job title or a page, and go there. Ctrl+K (⌘K) or "/"
 * from anywhere puts the cursor in it.
 *
 * What it finds and how it ranks is ui/globalSearch.ts. This is the box, the list and
 * where each hit opens:
 *
 *  - a person: their figures, in the drill-through, on the page you are on. The
 *    drill-through finds anyone the filter bar leaves out (`scope.byName`), so the bar
 *    stays; only under a slice, which does narrow it, is the bar cleared first;
 *  - a supervisor: their team, in the Supervisors sidebar;
 *  - a facility or facility group: its page on Facilities (#226);
 *  - a payer type or state: the Overview's sliced chart, narrowed to it;
 *  - a department or job title: the filter bar, set to it, on the Overview;
 *  - a page: the page;
 *  - a claim, by visit id or claim id (#604): Claim detail, searched to that visit. If
 *    the filters would hide it, the page sets them aside to show it, as its own "Not in
 *    your current filters" does (#207). Claim detail's rows load only once something
 *    that reads as an id is typed (ui/globalSearch.ts, claimQuery).
 *
 * Keyboard: ↑/↓ move, Enter opens, Esc clears and leaves. Below 900px the box is a
 * full-width row under the title, where an icon among the header's buttons would push
 * the title into the logo.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IconSearch, IconX } from '@tabler/icons-react'
import { useAppData } from '@/data/AppData'
import { personParts, useFilters, writeFilterParams } from '@/data/filters'
import { PF_KEY, VIEW_KEY } from '@/state/sharedView'
import { facilityHref } from './FacilityLink'
import { stateName } from '@/data/stateNames'
import { isNoClaim } from '@/data/noClaim'
import { useClaimDetail } from '@/data/claimDetail'
import { dateUs, int } from '@/format'
import {
  KIND_LABEL,
  KIND_OPENS,
  PER_GROUP,
  claimQuery,
  findClaimRows,
  searchAll,
  type SearchGroup,
  type SearchItem,
} from '@/ui/globalSearch'

/** A name that is a real facility or group, not a blank or the no-claim bucket. */
const real = (v: string | null | undefined): v is string =>
  !!v && v.trim() !== '' && v.trim().toUpperCase() !== 'UNKNOWN' && !isNoClaim(v)

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/** Whether a key press landed in something the reader types into. */
const typing = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))

export function GlobalSearch({
  pages,
  block = false,
  limited = false,
}: {
  /** The pages this reader has, as the rail lists them. */
  pages: readonly { path: string; label: string }[]
  /** Phone width: the full width of its row. */
  block?: boolean
  /**
   * A page-limited reader (access/pageLimit.tsx): only their pages and, with Claim
   * detail, visits. Every other hit opens a page or a person they do not have.
   */
  limited?: boolean
}) {
  const { derived, scope, crosstab, openPerson } = useAppData()
  const { filters, reset } = useFilters()
  const navigate = useNavigate()

  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const box = useRef<HTMLDivElement>(null)
  const id = useId()

  /** Everything findable, from the loaded bundle — already cut to the caller's access. */
  const items = useMemo<SearchItem[]>(() => {
    const out: SearchItem[] = []
    if (limited) {
      for (const p of pages) out.push({ kind: 'page', value: p.path, label: p.label })
      return out
    }
    for (const p of derived.people) {
      out.push({
        kind: 'person',
        value: p.employee,
        label: p.employee,
        detail: p.paylocityId,
        sub: [p.jobTitle, p.department].filter(Boolean).join(' · ') || null,
      })
    }
    for (const s of derived.supervisors) out.push({ kind: 'supervisor', value: s, label: s })
    for (const f of new Set(crosstab.facilities.filter(real))) out.push({ kind: 'facility', value: f, label: f })
    for (const g of derived.facilityGroups.filter(real)) out.push({ kind: 'facilityGroup', value: g, label: g })
    for (const p of derived.payers.filter(real)) out.push({ kind: 'payer', value: p, label: p })
    for (const d of derived.departments) out.push({ kind: 'department', value: d, label: d })
    for (const j of derived.jobTitles) out.push({ kind: 'jobTitle', value: j, label: j })
    for (const s of derived.states.filter(real)) {
      const name = stateName(s)
      out.push({ kind: 'state', value: s, label: name, detail: name === s ? null : s })
    }
    for (const p of pages) out.push({ kind: 'page', value: p.path, label: p.label })
    return out
  }, [derived, crosstab.facilities, pages, limited])

  /*
   * Claims (#604). Claim detail's rows are large, so they load the first time the box
   * holds something that reads as an id, and only for a reader who has Claim detail;
   * after that they stay (useClaimDetail shares one copy with the page).
   */
  const claimId = claimQuery(q)
  const canClaims = pages.some((p) => p.path === '/claim-detail')
  const [wantClaims, setWantClaims] = useState(false)
  useEffect(() => {
    if (claimId && canClaims) setWantClaims(true)
  }, [claimId, canClaims])
  const claims = useClaimDetail(wantClaims)
  const lookingUp = !!claimId && canClaims && !claims.data && !claims.error
  /** One hit per visit: its rows can be several (an owner each, or a payer each). */
  const claimGroup = useMemo<SearchGroup | null>(() => {
    const data = claims.data
    if (!claimId || !canClaims || !data) return null
    const { dict, cols } = data
    const byVisit = new Map<string, number[]>()
    for (const i of findClaimRows(cols.v, cols.fid, claimId)) {
      const list = byVisit.get(cols.v[i]!) ?? []
      list.push(i)
      byVisit.set(cols.v[i]!, list)
    }
    const found: SearchItem[] = [...byVisit].map(([v, rows]) => {
      const i = rows[0]!
      const fid = cols.fid?.[i]
      return {
        kind: 'claim',
        value: v,
        label: `Visit ${v}`,
        detail: fid != null && !v.includes('-') ? `claim ${fid}-${v}` : null,
        sub:
          [dict.f[cols.f[i]!], dict.pn[cols.pn[i]!], `billed ${dateUs(dict.d[cols.d[i]!] ?? null)}`, dict.e[cols.e[i]!]]
            .filter(Boolean)
            .join(' · ') + (rows.length > 1 ? ` · ${int(rows.length)} rows` : ''),
      }
    })
    if (!found.length) return null
    return { kind: 'claim', items: found.slice(0, PER_GROUP), more: Math.max(0, found.length - PER_GROUP) }
  }, [claimId, canClaims, claims.data])

  // An exact id is the likeliest thing meant, so its claims come first.
  const groups = useMemo(() => {
    const rest = searchAll(items, q)
    return claimGroup ? [claimGroup, ...rest] : rest
  }, [items, q, claimGroup])
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups])

  const close = useCallback(() => {
    setOpen(false)
    setQ('')
    setActive(0)
    input.current?.blur()
  }, [])

  /** The Overview with the filter bar as it is, plus `extra` on top. */
  const toOverview = (extra: Record<string, string>, next = filters) => {
    const params = new URLSearchParams()
    writeFilterParams(params, next)
    for (const [k, v] of Object.entries(extra)) params.set(k, v)
    const qs = params.toString()
    navigate({ pathname: '/', search: qs ? `?${qs}` : '' }, { state: { [PF_KEY]: personParts(next) } })
  }

  const go = (item: SearchItem) => {
    close()
    switch (item.kind) {
      case 'person':
        // Only a slice narrows the drill-through's lookup; then it has nobody to show.
        if (!scope.byName.has(item.value)) reset()
        openPerson(item.value)
        return
      case 'supervisor':
        // A bare path keeps the filter bar; the team rides in the history entry, as a
        // name must (ui/urlState.ts).
        navigate('/supervisors', { state: { [VIEW_KEY]: { team: item.value } } })
        return
      case 'facility':
        navigate(facilityHref({ facility: item.value }))
        return
      case 'facilityGroup':
        navigate(facilityHref({ group: item.value }))
        return
      case 'payer':
        toOverview({ payer: item.value })
        return
      case 'state':
        toOverview({ state: item.value })
        return
      case 'department':
        toOverview({}, { ...filters, department: [item.value] })
        return
      case 'jobTitle':
        toOverview({}, { ...filters, jobTitle: [item.value] })
        return
      case 'page':
        navigate(item.value)
        return
      case 'claim':
        // Claim detail's search, set to the visit, in the history entry (free text never
        // goes in the URL); `reveal` asks it to set aside any filter that hides it.
        navigate('/claim-detail', { state: { [VIEW_KEY]: { q: item.value.toLowerCase(), reveal: item.value } } })
        return
    }
  }

  // Ctrl+K / ⌘K from anywhere; "/" from anywhere that is not a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase()
      const chord = k === 'k' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey
      const slash = e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !typing(e.target)
      if (!chord && !slash) return
      e.preventDefault()
      setOpen(true)
      input.current?.focus()
      input.current?.select()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    document.getElementById(`${id}-o${active}`)?.scrollIntoView({ block: 'nearest' })
  }, [active, id])

  const shortcut = isMac ? '⌘K' : 'Ctrl K'
  const showList = open && q.trim() !== ''

  const field = (
    <div className="gsearch-field">
      <IconSearch size={15} stroke={1.75} aria-hidden="true" style={{ flex: 'none', color: 'var(--ink-3)' }} />
      <input
        ref={input}
        role="combobox"
        aria-expanded={showList}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={showList && flat[active] ? `${id}-o${active}` : undefined}
        aria-label="Search people, facilities, payers, departments, job titles, pages and visit ids"
        autoComplete="off"
        spellCheck={false}
        value={q}
        placeholder="Search people, facilities, visit ids…"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value)
          setActive(0)
          setOpen(true)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((a) => Math.min(a + 1, flat.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            const item = flat[active]
            if (item) go(item)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            close()
          }
        }}
      />
      {q ? (
        <button
          type="button"
          className="gsearch-clear"
          aria-label="Clear the search"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setQ('')
            setActive(0)
            input.current?.focus()
          }}
        >
          <IconX size={13} stroke={2} aria-hidden="true" />
        </button>
      ) : (
        !block && (
          <kbd className="gsearch-kbd" aria-hidden="true">
            {shortcut}
          </kbd>
        )
      )}
    </div>
  )

  let n = -1
  const list = showList && (
    <div className="gsearch-pop" data-testid="global-search-results">
      {lookingUp && (
        <p className="gsearch-empty" data-testid="global-search-claims-loading">
          Looking up claim {claimId} in Claim detail’s rows…
        </p>
      )}
      {claimId && canClaims && claims.error && !claims.data && (
        <p className="gsearch-empty">Claims can’t be searched right now: {claims.error}</p>
      )}
      {flat.length === 0 ? (
        !lookingUp && <p className="gsearch-empty">Nothing in your data matches “{q.trim()}”.</p>
      ) : (
        <ul id={`${id}-list`} role="listbox" aria-label="Search results" className="gsearch-list">
          {groups.map((g) => (
            <li key={g.kind} role="presentation">
              <div className="gsearch-head" role="presentation">
                <span>{KIND_LABEL[g.kind]}</span>
                {g.more > 0 && <span className="gsearch-more">and {g.more} more — keep typing</span>}
              </div>
              <ul role="group" aria-label={KIND_LABEL[g.kind]} className="gsearch-list">
                {g.items.map((item) => {
                  n += 1
                  const i = n
                  return (
                    <li
                      key={`${item.kind}|${item.value}`}
                      id={`${id}-o${i}`}
                      role="option"
                      aria-selected={i === active}
                      className={i === active ? 'gsearch-opt on' : 'gsearch-opt'}
                      // mousedown, not click: the box's blur would come first.
                      onMouseDown={(e) => {
                        e.preventDefault()
                        go(item)
                      }}
                      onMouseMove={() => i !== active && setActive(i)}
                    >
                      <span className="gsearch-label">
                        {item.label}
                        {item.detail && <span className="gsearch-detail"> {item.detail}</span>}
                        {item.sub && <span className="gsearch-sub">{item.sub}</span>}
                      </span>
                      <span className="gsearch-opens">{KIND_OPENS[item.kind]}</span>
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
      <p className="gsearch-foot">
        ↑↓ to move · Enter to open · Esc to close · searches only the data you can see
      </p>
    </div>
  )

  return (
    <div ref={box} className={block ? 'gsearch gsearch-block' : 'gsearch'} role="search">
      {field}
      {list}
    </div>
  )
}
